import {action, makeObservable, observable, runInAction} from "mobx";
import {ITransactionService} from "../../../services/transactionService";
import {IUserService} from "../../../services/userService";
import ProcessedBookingGroups from "./models/processedBookingGroups";
import ProcessedBookingGroup from "./models/processedBookingGroup";
import ProcessedBooking from "./models/processedBooking";

export interface ITransactionsDomainStore {
  /**
   * Transactions fetched from backend.
   */
  transactionsList: ProcessedBookingGroups;
  /**
   * Method that triggers fetching of transactions from the server.
   * @param selectedDate This would contain first date of the month for which the transactions are to be fetched.
   * @returns List of transactions for a specific month.
   */
  loadTransactions: (startDate: Date, endDate: Date) => Promise<void>;

  /**
   * Get transactions by start and end dates.
   */
  getTransactions: (
    startDate: Date,
    endDate: Date
  ) => Promise<ProcessedBookingGroups>;
}

export class TransactionsDomainStore implements ITransactionsDomainStore {
  private readonly transactionService: ITransactionService;
  private readonly userService: IUserService;
  transactionsList: ProcessedBookingGroups = new ProcessedBookingGroups(
    [],
    0,
    0,
    false
  );
  private transactionsRequestId = 0;

  constructor(
    transactionService: ITransactionService,
    userService: IUserService
  ) {
    makeObservable(this, {
      transactionsList: observable,
      loadTransactions: action
    });
    this.transactionService = transactionService;
    this.userService = userService;
  }

  loadTransactions = async (startDate: Date, endDate: Date) => {
    const requestId = ++this.transactionsRequestId;
    const transactions = await this.getTransactions(startDate, endDate);
    runInAction(() => {
      if (requestId === this.transactionsRequestId) {
        this.transactionsList = transactions;
      }
    });
  };

  getTransactions = async (
    startDate: Date,
    endDate: Date
  ): Promise<ProcessedBookingGroups> => {
    const selectedVillageId = this.userService.getSelectedVillage()?.id!;
    if (!selectedVillageId) {
      return new ProcessedBookingGroups([], 0, 0, false);
    }
    const data = await this.transactionService.getTransactions(
      selectedVillageId,
      startDate,
      endDate
    );

    const mapProcessedBooking = (booking: any) =>
      new ProcessedBooking(
        booking.id,
        booking.bookingId,
        booking.price,
        booking.payeeId,
        booking.payeeName,
        booking.eventId,
        booking.eventTitle,
        booking.isSuccess,
        booking.transactionDetails,
        booking.timestamp,
        booking.createdDate,
        booking.eventOccurrenceDate
      );

    const mapBookingGroup = (event: any) =>
      new ProcessedBookingGroup(
        event.eventId,
        event.eventOccurrenceDate,
        event.eventTitle,
        event.processedBookings.map(mapProcessedBooking),
        event.eventPrice,
        event.processedBookingsCount,
        event.totalPrice,
        event.isSuccess
      );

    return new ProcessedBookingGroups(
      data.events.map(mapBookingGroup),
      data.totalPrice,
      data.totalCount,
      data.isSuccess
    );
  };
}
